import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

type ToastType = 'success' | 'error'

export interface Toast {
  type: ToastType
  message: string
}

interface ToastContextValue {
  toast: Toast | null
  notify: (type: ToastType, message: string) => void
  dismiss: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const AUTO_DISMISS_MS = 6000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const dismiss = useCallback(() => {
    clearTimer()
    setToast(null)
  }, [])

  const notify = useCallback((type: ToastType, message: string) => {
    clearTimer()
    setToast({ type, message })
    timerRef.current = setTimeout(() => setToast(null), AUTO_DISMISS_MS)
  }, [])

  useEffect(() => clearTimer, [])

  return (
    <ToastContext.Provider value={{ toast, notify, dismiss }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside ToastProvider')
  return ctx
}
